import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { connApi, spaceApi } from '../services/api';
import { useSpaceId } from '../contexts/SpaceContext';
import type { CreateIntegrationInput, UpdateIntegrationInput } from '../types/integrations';

function useIntegrationApi() {
  const spaceId = useSpaceId();
  return useMemo(() => spaceApi(spaceId).integrations, [spaceId]);
}

/**
 * Provider catalog. Org-global on the backend — used by both space-side and
 * admin surfaces. Cached aggressively because providers are essentially
 * static content.
 */
export function useProviders() {
  return useQuery({
    queryKey: ['conn', 'providers'],
    queryFn: () => connApi.providers.list(),
    staleTime: 60 * 60 * 1000,
  });
}

export function useProviderSetupGuide(name: string | undefined) {
  return useQuery({
    queryKey: ['conn', 'provider-setup-guide', name],
    queryFn: () => connApi.providers.getSetupGuide(name!),
    enabled: !!name,
    staleTime: 60 * 60 * 1000,
  });
}

/**
 * Quick setup guide — a tight, opinionated walkthrough rendered prominently
 * on the form, success state, and detail page. Backend resolves to null for
 * providers that don't ship a quick guide; consumers render nothing in that
 * case.
 */
export function useProviderQuickSetupGuide(name: string | undefined) {
  return useQuery({
    queryKey: ['conn', 'provider-quick-setup-guide', name],
    queryFn: () => connApi.providers.getQuickSetupGuide(name!),
    enabled: !!name,
    staleTime: 60 * 60 * 1000,
  });
}

export function useIntegrations() {
  const spaceId = useSpaceId();
  const api = useIntegrationApi();
  return useQuery({
    queryKey: ['space', spaceId, 'integrations'],
    queryFn: () => api.list(),
  });
}

export function useIntegration(integrationId: string | undefined) {
  const spaceId = useSpaceId();
  const api = useIntegrationApi();
  return useQuery({
    queryKey: ['space', spaceId, 'integrations', integrationId],
    queryFn: () => api.get(integrationId!),
    enabled: !!integrationId,
  });
}

export function useIntegrationEvents(integrationId: string | undefined) {
  const spaceId = useSpaceId();
  const api = useIntegrationApi();
  return useQuery({
    queryKey: ['space', spaceId, 'integrations', integrationId, 'events'],
    queryFn: () => api.listEvents(integrationId!),
    enabled: !!integrationId,
    refetchInterval: 30000,
  });
}

export function useIntegrationEventDetail(
  integrationId: string | undefined,
  eventId: string | null,
) {
  const spaceId = useSpaceId();
  const api = useIntegrationApi();
  return useQuery({
    queryKey: ['space', spaceId, 'integrations', integrationId, 'events', eventId],
    queryFn: () => api.getEvent(integrationId!, eventId!),
    enabled: !!integrationId && !!eventId,
    // Delivery details are immutable once recorded — no need to refetch.
    staleTime: Infinity,
  });
}

export function useIntegrationRedeliver(integrationId: string | undefined) {
  const spaceId = useSpaceId();
  const api = useIntegrationApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) => api.redeliverEvent(integrationId!, eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['space', spaceId, 'integrations', integrationId, 'events'],
      });
      queryClient.invalidateQueries({
        queryKey: ['space', spaceId, 'integrations', integrationId],
      });
    },
  });
}

export function useCreateIntegration() {
  const spaceId = useSpaceId();
  const api = useIntegrationApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateIntegrationInput) => api.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'integrations'] });
    },
  });
}

export function useUpdateIntegration() {
  const spaceId = useSpaceId();
  const api = useIntegrationApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      integrationId,
      input,
    }: {
      integrationId: string;
      input: UpdateIntegrationInput;
    }) => api.update(integrationId, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'integrations'] });
      queryClient.invalidateQueries({
        queryKey: ['space', spaceId, 'integrations', variables.integrationId],
      });
    },
  });
}

export function useDeleteIntegration() {
  const spaceId = useSpaceId();
  const api = useIntegrationApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (integrationId: string) => api.delete(integrationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'integrations'] });
    },
  });
}
